import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Crown,
  Star,
  Check,
  X,
  Loader2,
  CreditCard,
  ArrowLeft,
  Sparkles,
  Shield,
  Zap,
  Gift,
  Calendar,
  ChevronRight,
  AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MembershipUpgrade - Dedicated page for upgrading/changing membership with Stripe payment
 */
const MembershipUpgrade = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [packages, setPackages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  // Get current membership info
  const currentTier = user?.membership_tier || 'free';
  const memberClass = user?.member_class || 'guest';
  const expiresAt = user?.membership_expires_at;
  const isBetaUser = user?.beta_membership_active;

  // Fetch available packages
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${API}/api/payments/membership/packages`);
        if (response.ok) {
          const data = await response.json();
          setPackages(data.packages);
        }
      } catch (error) {
        console.error('Error fetching packages:', error);
        toast.error('Failed to load membership options');
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  // Start checkout process
  const handleUpgrade = async (packageId) => {
    if (!token) {
      toast.error('Please log in to upgrade your membership');
      navigate('/login', { state: { from: '/membership/upgrade' } });
      return;
    }

    setCheckoutLoading(packageId);
    try {
      const response = await fetch(`${API}/api/payments/membership/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start checkout');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  // Get display package based on billing cycle
  const getDisplayPackage = (baseId) => {
    if (!packages) return null;
    const packageId = billingCycle === 'yearly' ? `${baseId}_yearly` : baseId;
    return packages[packageId];
  };

  // Calculate savings for yearly
  const calculateSavings = (baseId) => {
    if (!packages) return 0;
    const monthly = packages[baseId];
    const yearly = packages[`${baseId}_yearly`];
    if (!monthly || !yearly) return 0;
    return (monthly.amount * 12) - yearly.amount;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="membership-upgrade-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white py-12">
        <div className="container mx-auto px-4">
          <Link 
            to="/settings" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Settings
          </Link>
          
          <div className="max-w-2xl">
            <h1 className="text-3xl font-heading font-medium mb-2">
              Upgrade Your Membership
            </h1>
            <p className="text-white/80">
              Choose a plan that works for you. Unlock premium features and support the community.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Current Status Card */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Current Membership</p>
                <div className="flex items-center gap-2">
                  <Badge variant={currentTier === 'free' ? 'secondary' : 'default'} className="text-sm">
                    {currentTier === 'premium' ? (
                      <><Crown className="w-3 h-3 mr-1" /> Premium</>
                    ) : currentTier === 'paid' ? (
                      <><Star className="w-3 h-3 mr-1" /> Full Member</>
                    ) : (
                      'Free'
                    )}
                  </Badge>
                  {isBetaUser && (
                    <Badge variant="outline" className="text-green-600 border-green-300">
                      <Gift className="w-3 h-3 mr-1" /> Beta Benefits Active
                    </Badge>
                  )}
                </div>
              </div>
              
              {expiresAt && (
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Expires</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(expiresAt).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8">
          <Tabs value={billingCycle} onValueChange={setBillingCycle} className="w-auto">
            <TabsList>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="yearly" className="relative">
                Yearly
                <Badge className="absolute -top-2 -right-2 bg-green-500 text-[10px] px-1.5">
                  Save 17%
                </Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Full Member Plan */}
          <Card className={`relative ${currentTier === 'paid' ? 'ring-2 ring-primary' : ''}`}>
            {currentTier === 'paid' && (
              <Badge className="absolute -top-3 left-4 bg-primary">Current Plan</Badge>
            )}
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle>Full Member</CardTitle>
                  <CardDescription>Full club membership</CardDescription>
                </div>
              </div>
              <div className="mt-4">
                <span className="text-4xl font-bold">
                  ${getDisplayPackage('paid')?.amount.toFixed(2)}
                </span>
                <span className="text-muted-foreground">
                  /{billingCycle === 'yearly' ? 'year' : 'month'}
                </span>
                {billingCycle === 'yearly' && (
                  <p className="text-sm text-green-600 mt-1">
                    Save ${calculateSavings('paid').toFixed(2)}/year
                  </p>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                {getDisplayPackage('paid')?.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  No Google ads (sponsor ads only)
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Vote in club elections
                </li>
              </ul>
              
              <Button 
                className="w-full"
                onClick={() => handleUpgrade(billingCycle === 'yearly' ? 'paid_yearly' : 'paid')}
                disabled={checkoutLoading || currentTier === 'paid'}
                data-testid="upgrade-paid-btn"
              >
                {checkoutLoading === (billingCycle === 'yearly' ? 'paid_yearly' : 'paid') ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : currentTier === 'paid' ? (
                  'Current Plan'
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    {currentTier === 'premium' ? 'Switch to Full' : 'Upgrade to Full'}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Premium Plan */}
          <Card className={`relative border-amber-300 ${currentTier === 'premium' ? 'ring-2 ring-amber-500' : ''}`}>
            <div className="absolute -top-3 right-4">
              <Badge className="bg-amber-500">Most Popular</Badge>
            </div>
            {currentTier === 'premium' && (
              <Badge className="absolute -top-3 left-4 bg-amber-500">Current Plan</Badge>
            )}
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle>Premium Professional</CardTitle>
                  <CardDescription>The ultimate experience</CardDescription>
                </div>
              </div>
              <div className="mt-4">
                <span className="text-4xl font-bold">
                  ${getDisplayPackage('premium')?.amount.toFixed(2)}
                </span>
                <span className="text-muted-foreground">
                  /{billingCycle === 'yearly' ? 'year' : 'month'}
                </span>
                {billingCycle === 'yearly' && (
                  <p className="text-sm text-green-600 mt-1">
                    Save ${calculateSavings('premium').toFixed(2)}/year
                  </p>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                {getDisplayPackage('premium')?.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Completely ad-free experience
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Early access to new features
                </li>
              </ul>
              
              <Button 
                className="w-full bg-amber-500 hover:bg-amber-600"
                onClick={() => handleUpgrade(billingCycle === 'yearly' ? 'premium_yearly' : 'premium')}
                disabled={checkoutLoading || currentTier === 'premium'}
                data-testid="upgrade-premium-btn"
              >
                {checkoutLoading === (billingCycle === 'yearly' ? 'premium_yearly' : 'premium') ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : currentTier === 'premium' ? (
                  'Current Plan'
                ) : (
                  <>
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade to Premium
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Stripe Security Notice */}
        <Card className="mt-8 border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm text-green-800">Your card data is safe — we never see it</p>
                <p className="text-xs text-muted-foreground mt-1">
                  All payments are processed securely by <strong>Stripe</strong>, an industry-leading payment processor. 
                  When you check out, you're redirected to Stripe's secure payment page. Your card number, CVV, and 
                  billing details are entered directly on Stripe's servers — they never touch or pass through our systems. 
                  We only receive a confirmation that your payment was successful.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          <Card className="bg-muted/50">
            <CardContent className="p-4 flex items-start gap-3">
              <Shield className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Secure Checkout</p>
                <p className="text-xs text-muted-foreground">
                  Powered by Stripe. You'll be redirected to Stripe's secure page — we never see your card data.
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-muted/50">
            <CardContent className="p-4 flex items-start gap-3">
              <Zap className="w-5 h-5 text-amber-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Instant Activation</p>
                <p className="text-xs text-muted-foreground">
                  Your membership is activated immediately after payment.
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-muted/50">
            <CardContent className="p-4 flex items-start gap-3">
              <Gift className="w-5 h-5 text-purple-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Have a Referral?</p>
                <p className="text-xs text-muted-foreground">
                  Referral discounts are automatically applied at checkout.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Link */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground text-sm">
            Have questions? Check out our{' '}
            <Link to="/faq#membership" className="text-primary hover:underline">
              Membership FAQ
            </Link>
            {' '}or{' '}
            <Link to="/membership" className="text-primary hover:underline">
              compare all features
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default MembershipUpgrade;
